import { useEffect, useRef, useState } from "react";

// ===== MINI SIDEBAR (COLLAPSE) + MOBILE OPEN/CLOSE BEHAVIOR =====
// Hover-to-expand on desktop, swipe + overlay/escape close on mobile.
export const useSidebarCollapse = ({
  isMobile,
  isRTL,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const sidebarRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(false);
      }, 250);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
      }, 150);
    }
  };

  const handleSidebarToggle = (event) => {
    event.stopPropagation();
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Never leave the sidebar in the collapsed state on mobile
  useEffect(() => {
    if (isMobile && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMobile, isCollapsed]);

  // ===== TOUCH SWIPE STATE =====
  const [touchStart, setTouchStart] = useState({ x: null, y: null });
  const handleTouchStart = (e) =>
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  const handleTouchMove = (e) => {
    if (touchStart.x === null || touchStart.y === null) return;
    const diffX = touchStart.x - e.touches[0].clientX;
    const diffY = touchStart.y - e.touches[0].clientY;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (isRTL) {
        if (diffX > 0 && !sidebarOpen) setSidebarOpen(true);
        if (diffX < 0 && sidebarOpen) setSidebarOpen(false);
      } else {
        if (diffX < 0 && !sidebarOpen) setSidebarOpen(true);
        if (diffX > 0 && sidebarOpen) setSidebarOpen(false);
      }
      setTouchStart({ x: null, y: null });
    }
  };
  const handleTouchEnd = () => setTouchStart({ x: null, y: null });

  // Close the mobile sidebar when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const menuButton = event.target.closest(".menu-btn");
        const collapseButton = event.target.closest(".collapse-btn");
        if (!menuButton && !collapseButton && sidebarOpen && isMobile) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen, isMobile]);

  return {
    isCollapsed,
    setIsCollapsed,
    hoveredNavItem,
    setHoveredNavItem,
    sidebarRef,
    handleMouseEnter,
    handleMouseLeave,
    handleSidebarToggle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
